import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Send, Bot } from "lucide-react";

const SUGGERIMENTI = ["Chi guidava il furgone targato GW343PG il 03/09/2026?", "In che cantiere era Domenighini ieri?", "Chi era assente oggi e perché?", "Quali mezzi c'erano a Empoli il 03/09?"];

// Iter 112: domande in linguaggio naturale sullo storico dei programmi
export default function ChiediDialog({ open, onClose }) {
  const [msgs, setMsgs] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [sid, setSid] = useState("");
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const ask = async (text) => {
    const domanda = (text || q).trim();
    if (!domanda || busy) return;
    setQ(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: domanda }]);
    try {
      const { data } = await api.post("/programma/chiedi", { domanda, session_id: sid }, { timeout: 120000 });
      setSid(data.session_id);
      setMsgs((m) => [...m, { role: "assistant", text: data.risposta, giorni: data.giorni_consultati }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", text: e?.response?.data?.detail || "Non sono riuscito a rispondere, riprova.", error: true }]);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" data-testid="chiedi-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Bot className="h-5 w-5 text-[#0044FF]" /> Chiedi all'assistente</DialogTitle>
          <DialogDescription>Risponde solo con i dati dei programmi salvati: chi era dove, mezzi, assenze. Indica la data nella domanda (es. 03/09/2026, ieri, oggi).</DialogDescription>
        </DialogHeader>
        <div className="flex-1 min-h-[220px] max-h-[50vh] overflow-y-auto space-y-2 rounded-xl bg-slate-50 border p-3" data-testid="chiedi-messages">
          {msgs.length === 0 && (
            <div className="space-y-1.5">
              <div className="text-xs text-slate-500">Prova a chiedere:</div>
              {SUGGERIMENTI.map((s) => <button key={s} onClick={() => ask(s)} className="block w-full text-left text-xs rounded-lg bg-white border px-2.5 py-1.5 hover:border-[#0044FF]" data-testid="chiedi-suggerimento">{s}</button>)}
            </div>
          )}
          {msgs.map((m, i) => (
            <div key={i} className={`max-w-[92%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "ml-auto bg-[#0044FF] text-white" : m.error ? "bg-red-50 text-red-800 border border-red-200" : "bg-white border text-slate-800"}`} data-testid={`chiedi-msg-${m.role}`}>
              {m.text.replace(/\*\*/g, "")}
              {m.giorni?.length > 0 && <div className="text-[10px] text-slate-400 mt-1">giorni consultati: {m.giorni.join(", ")}</div>}
            </div>
          ))}
          {busy && <div className="flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> sto controllando i programmi…</div>}
          <div ref={endRef} />
        </div>
        <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Es. chi guidava il furgone (23) il 03/09?" className="flex-1 h-10 rounded-full border px-4 text-sm outline-none focus:border-[#0044FF]" data-testid="chiedi-input" disabled={busy} />
          <Button type="submit" disabled={busy || !q.trim()} className="rounded-full bg-[#0044FF] hover:bg-[#0033CC] h-10 px-4" data-testid="chiedi-send"><Send className="h-4 w-4" /></Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
